import * as THREE from 'three';
import { LightProbeGenerator } from 'three/examples/jsm/lights/LightProbeGenerator.js';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';

import vertexShader from './shaders/teller.vert'
import fragmentShader from './shaders/teller.frag'
import shellVertexShader from './shaders/teller_shell.vert'
import shellFragmentShader from './shaders/teller_shell.frag'
import type { ResponsiveTransformConfig } from '../../tellerAssistantConfig';

type Vector3Like = { x: number; y: number; z: number };

interface TellerAssistantOptions {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  anchor?: THREE.Object3D;
  position?: Vector3Like;
  rotationY?: number;
  scale?: number;
  useEnvironmentLighting?: boolean;
  environmentLightingIntensity?: number;
  panoramaTextureUrl?: string; // URL for panoramic HDR texture (e.g., 'sky.hdr')
  isMobile?: boolean; // Quality-dependent geometry/particles reduction
}

// Default parameters - synced with tellerAssistantConfig.ts
const defaultParams = {
  // Goo deformation
  gooScale: 10,
  gooAmount: 0.14129404669595932,
  gooSpeed: 0.11269061773067976,
  gooPoleAmount: 1.0,
  // Wave deformation
  waveScale: 0.7994055311449987,
  waveFrequency: 1.633497027655725,
  waveAmount: 1.8419919014387869,
  waveSpeed: 0.476608942879297,
  wavePoleAmount: 0.18,
  // Visual
  color: '#d9c7a7',
  rotationSpeed: 0.002,
  showNormals: false,
  // Hover animation
  hoverAmplitude: 0.0007,
  // Light
  lightX: 0.2,
  lightY: 0.4,
  lightZ: 0.5,
  ambientLightColor: '#ffe59c',
  directionalLightColor: '#ffeebf',
  ambientLightIntensity: 0.17,
  directionalLightIntensity: 1.45,
  // Particles sphere
  particlesCount: 28000,
  particlesColor: '#fff3c9',
  particlesSize: 0.01,
  particlesRadius: 1.0751270784871199,
  particlesNoiseScale: 2,
  particlesNoiseSpeed: 0.21400878780046523,
  particlesNoiseAmount: 0.25157232704402516,
};

export class TellerAssistantShader {
  private readonly position?: Vector3Like;
  private readonly rotationY?: number;
  private readonly scale?: number;
  private readonly scene: THREE.Scene;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly anchor?: THREE.Object3D;
  private readonly lookTarget = new THREE.Vector3();
  private enabled = false;
  private renderLayer = 0;

  private mesh: THREE.Mesh;
  private shell: THREE.Points;
  private meshUniforms: { [key: string]: THREE.IUniform };
  private shellUniforms: { [key: string]: THREE.IUniform };
  private basePosition = new THREE.Vector3();
  private hoverTime = 0;
  private disposed = false;
  private params = { ...defaultParams };
  private geometryScale: number;
  private useEnvironmentLighting: boolean;
  private environmentLightingIntensity: number;
  private environmentRotationY = 0;
  private readonly panoramaTextureUrl?: string;
  private currentPosition: Vector3Like;
  private currentRotationY: number;
  private currentScale: number;
  private cubeCamera?: THREE.CubeCamera;
  private cubeRenderTarget?: THREE.WebGLCubeRenderTarget;
  private panoramaTexture?: THREE.Texture;
  private lightProbe?: THREE.LightProbe;
  private lightProbeUpdateTime = 0;
  private readonly lightProbeUpdateInterval = 1.0; // Update once per second
  private lightProbeReady = false; // Light probe is computed once at load, not per-frame

  // Cached empty scene for light probe generation (avoids allocation each time)
  private cachedEmptyScene?: THREE.Scene;

  // Quality tier flag
  private readonly isMobile: boolean;

  // Reusable temp objects to avoid per-frame allocations (GC pressure)
  private readonly _tmpCamPos = new THREE.Vector3();
  private readonly _tmpCamQuat = new THREE.Quaternion();
  private readonly _tmpWorldVel = new THREE.Vector3();
  private readonly _tmpLocalVel = new THREE.Vector3();
  private readonly _tmpInvQuat = new THREE.Quaternion();
  private readonly _tmpTargetOffset = new THREE.Vector3();
  private readonly _tmpFinalPos = new THREE.Vector3();

  // Inertia system for smooth camera following (camera-local-space lag)
  private lastCameraPosition = new THREE.Vector3();
  private lastCameraRotation = new THREE.Quaternion();
  private smoothedLocalOffset = new THREE.Vector3(); // Smoothed offset in camera local space
  private readonly positionSmoothing = 0.15; // Lower = more lag (0-1), was 0.25
  private readonly maxInertiaOffset = 0.004; // Maximum offset in camera local space (very subtle), was 0.01
  private readonly minVelocityThreshold = 0.0005; // Minimum velocity to apply inertia effect
  private inTransitionMode = false; // When true, inertia is reversed (ball leads camera)

  constructor(options: TellerAssistantOptions) {
    this.position = options.position;
    this.rotationY = options.rotationY;
    this.scale = options.scale;
    this.scene = options.scene;
    this.renderer = options.renderer;
    this.anchor = options.anchor;
    this.isMobile = options.isMobile ?? false;
    this.useEnvironmentLighting = options.useEnvironmentLighting ?? false;
    this.environmentLightingIntensity = options.environmentLightingIntensity ?? 1.0;
    this.panoramaTextureUrl = options.panoramaTextureUrl;

    // Quality-dependent particle count: 12K desktop / 6K mobile
    this.params.particlesCount = this.isMobile ? 6000 : 12000;

    this.geometryScale = typeof this.scale === 'number' ? this.scale : 1.0;
    this.currentPosition = options.position ?? { x: 0, y: 0, z: 0 };
    this.currentRotationY = options.rotationY ?? 0;
    this.currentScale = this.geometryScale;

    // Initialize cubeCamera and cubeRenderTarget if environment lighting is enabled
    if (this.useEnvironmentLighting) {
      this.cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256);
      this.cubeCamera = new THREE.CubeCamera(0.01, 1000, this.cubeRenderTarget);
    }

    // Initialize SH coefficients array (9 vec3 for L2 spherical harmonics)
    const shCoefficients: THREE.Vector3[] = [];
    for (let i = 0; i < 9; i++) {
      shCoefficients.push(new THREE.Vector3(0, 0, 0));
    }

    // Initialize uniforms - deformations work with normalized coordinates (radius 1.0)
    // Scale will be applied via object.scale, so deformations stay proportional
    this.meshUniforms = {
      time: { value: 0 },
      gooScale: { value: this.params.gooScale },
      gooAmount: { value: this.params.gooAmount },
      gooSpeed: { value: this.params.gooSpeed },
      gooPoleAmount: { value: this.params.gooPoleAmount },
      waveScale: { value: this.params.waveScale },
      waveFrequency: { value: this.params.waveFrequency },
      waveAmount: { value: this.params.waveAmount },
      waveSpeed: { value: this.params.waveSpeed },
      wavePoleAmount: { value: this.params.wavePoleAmount },
      color: { value: new THREE.Color(this.params.color) },
      lightDirection: { value: new THREE.Vector3(this.params.lightX, this.params.lightY, this.params.lightZ).normalize() },
      ambientLightColor: { value: new THREE.Color(this.params.ambientLightColor) },
      directionalLightColor: { value: new THREE.Color(this.params.directionalLightColor) },
      ambientLightIntensity: { value: this.params.ambientLightIntensity },
      directionalLightIntensity: { value: this.params.directionalLightIntensity },
      showNormals: { value: this.params.showNormals },
      useEnvironmentLighting: { value: this.useEnvironmentLighting },
      environmentLightingIntensity: { value: this.environmentLightingIntensity },
      shLightProbe: { value: shCoefficients }
    };

    this.shellUniforms = {
      uTime: { value: 0 },
      uNoiseScale: { value: this.params.particlesNoiseScale * 0.35 },
      uNoiseAmplitude: { value: this.params.particlesNoiseAmount * 3.3 },
      uNoiseSpeed: { value: this.params.particlesNoiseSpeed },
      uRadius: { value: this.params.particlesRadius * this.geometryScale },
      uColor: { value: new THREE.Color(this.params.particlesColor) },
      uScale: { value: this.params.particlesSize * 50 * this.geometryScale }
    };

    this.mesh = this.createMesh();
    this.shell = this.createShell();
    this.mesh.layers.set(this.renderLayer);
    this.shell.layers.set(this.renderLayer);
  }

  async load(environmentReady?: Promise<unknown>): Promise<void> {
    await environmentReady?.catch(() => undefined);

    // Apply scale via object.scale - this way deformations work with normalized coordinates
    if (this.geometryScale !== 1.0) {
      this.mesh.scale.setScalar(this.geometryScale);
      // For shell, scale uRadius uniform (not object scale to avoid double scaling via modelMatrix)
      this.shellUniforms.uRadius.value = this.params.particlesRadius * this.geometryScale;
      this.shellUniforms.uScale.value = this.params.particlesSize * 50 * this.geometryScale;
    }

    this.setInitialTransform(this.mesh);
    this.setInitialTransform(this.shell);

    const targetParent = this.anchor ?? this.scene;
    targetParent.add(this.mesh);
    targetParent.add(this.shell);

    this.basePosition.copy(this.mesh.position);
    this.mesh.visible = this.enabled;
    this.shell.visible = this.enabled;

    // Initialize environment lighting if enabled
    if (this.useEnvironmentLighting) {
      await this.loadPanoramaTexture();
    }
  }

  update(delta: number, camera: THREE.Camera): void {
    if (this.disposed || !this.enabled) {
      return;
    }

    if (!this.meshUniforms || !this.shellUniforms) {
      return;
    }

    const time = performance.now() * 0.001;
    if (this.meshUniforms.time) {
      this.meshUniforms.time.value = time;
    }
    if (this.shellUniforms.uTime) {
      this.shellUniforms.uTime.value = time;
    }

    // Camera-local-space inertia: ball lags with offset in camera's local coordinate system
    if (this.anchor && this.anchor instanceof THREE.Camera) {
      this.anchor.getWorldPosition(this._tmpCamPos);
      this.anchor.getWorldQuaternion(this._tmpCamQuat);
      
      // Initialize on first frame
      if (this.lastCameraPosition.lengthSq() === 0) {
        this.lastCameraPosition.copy(this._tmpCamPos);
        this.lastCameraRotation.copy(this._tmpCamQuat);
        this.smoothedLocalOffset.set(0, 0, 0);
      }
      
      // Calculate camera velocity in world space (reuse _tmpWorldVel)
      const deltaSafe = Math.max(delta, 0.001);
      this._tmpWorldVel.copy(this._tmpCamPos).sub(this.lastCameraPosition).divideScalar(deltaSafe);
      
      // Transform velocity to camera local space (rotation only, no translation)
      this._tmpInvQuat.copy(this._tmpCamQuat).invert();
      this._tmpLocalVel.copy(this._tmpWorldVel).applyQuaternion(this._tmpInvQuat);
      
      // Calculate target offset based on velocity
      // Normal mode: offset opposite to velocity (ball lags behind)
      // Transition mode: offset same as velocity (ball leads, "pulling" camera to POI)
      const velocityMagnitude = this._tmpLocalVel.length();
      this._tmpTargetOffset.set(0, 0, 0);
      
      if (velocityMagnitude > this.minVelocityThreshold) {
        const offsetStrength = Math.min(velocityMagnitude * 2.0, 1.0); // Scale factor
        // Direction: -1 = lag behind (normal), +1 = lead (transition)
        const direction = this.inTransitionMode ? 1 : -1;
        // During transition, use stronger offset (3x) to compensate for fast camera movement
        const transitionBoost = this.inTransitionMode ? 3.0 : 1.0;
        const effectiveMaxOffset = this.maxInertiaOffset * transitionBoost;
        this._tmpTargetOffset.copy(this._tmpLocalVel).multiplyScalar(direction * offsetStrength * effectiveMaxOffset);
        
        // Clamp target offset
        if (this._tmpTargetOffset.length() > effectiveMaxOffset) {
          this._tmpTargetOffset.normalize().multiplyScalar(effectiveMaxOffset);
        }
      }
      
      // Smoothly lerp current offset towards target offset in local space
      // Faster ramp-up during POI transition for more responsive "pulling" effect
      const smoothingFactor = this.inTransitionMode ? 0.4 : this.positionSmoothing;
      this.smoothedLocalOffset.lerp(this._tmpTargetOffset, smoothingFactor);
      
      // Apply offset to base position (both in camera local space)
      this._tmpFinalPos.set(
        this.currentPosition.x + this.smoothedLocalOffset.x,
        this.currentPosition.y + this.smoothedLocalOffset.y,
        this.currentPosition.z + this.smoothedLocalOffset.z
      );
      
      this.mesh.position.copy(this._tmpFinalPos);
      this.shell.position.copy(this._tmpFinalPos);
      this.basePosition.copy(this._tmpFinalPos);
      
      // Update last camera state
      this.lastCameraPosition.copy(this._tmpCamPos);
      this.lastCameraRotation.copy(this._tmpCamQuat);
    } else {
      // No anchor or not a camera - use original behavior
      this.mesh.position.set(this.currentPosition.x, this.currentPosition.y, this.currentPosition.z);
      this.shell.position.set(this.currentPosition.x, this.currentPosition.y, this.currentPosition.z);
      this.basePosition.copy(this.mesh.position);
    }

    this.hoverTime += delta;
    const hoverOffset = Math.sin(this.hoverTime * 1.2) * this.params.hoverAmplitude;
    this.mesh.position.y = this.basePosition.y + hoverOffset;
    this.shell.position.y = this.basePosition.y + hoverOffset;

    // Optional rotation
    this.mesh.rotation.y += this.params.rotationSpeed;
    this.shell.rotation.y += this.params.rotationSpeed;

    if (!this.anchor) {
      this.lookTarget.copy(camera.position);
      this.lookTarget.y = this.mesh.position.y;
      this.mesh.lookAt(this.lookTarget);
      this.shell.lookAt(this.lookTarget);
    } else {
      this.mesh.rotation.set(0, 0, 0);
      this.shell.rotation.set(0, 0, 0);
    }

    // Light probe is computed once at load time (see loadPanoramaTexture).
    // No periodic update needed — the panorama is static.
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.mesh) {
      this.mesh.visible = enabled;
    }
    if (this.shell) {
      this.shell.visible = enabled;
    }
  }

  setRenderLayer(layer: number): void {
    this.renderLayer = layer;
    this.mesh.layers.set(layer);
    this.shell.layers.set(layer);
  }

  needsRerender(): boolean {
    return !this.disposed && this.enabled && this.mesh.parent !== null && this.shell.parent !== null;
  }

  /**
   * Set transition mode for inertia behavior.
   * When true, ball leads the camera (appears to "pull" towards destination).
   * When false, ball lags behind (normal behavior).
   */
  setTransitionMode(inTransition: boolean): void {
    this.inTransitionMode = inTransition;
  }

  /**
   * Update assistant position based on viewport dimensions and responsive config.
   * Converts pixel-based positioning to camera-local coordinates, compensating
   * for the camera's principal-point shift caused by the viewOffset.
   *
   * The ball is anchored to the curtain (chat panel) top boundary on screen.
   * Because the camera projection is shifted (via PrincipalPointController),
   * a naive camera-local position doesn't map 1:1 to screen pixels. We
   * replicate the same py shift formula to compensate.
   *
   * @param width - Viewport width in pixels
   * @param height - Viewport height in pixels
   * @param fov - Camera field of view in degrees
   * @param _isMobile - Whether we're on a mobile device (kept for compat, branching uses paddingRight)
   * @param curtainBoundaryPx - Curtain top edge in pixels from the bottom of the viewport (viewOffset.bottom)
   * @param viewOffsetTopPx - Top inset in pixels (viewOffset.top, typically safeArea.top)
   * @param config - Responsive transform config (desktop or mobile)
   */
  updatePositionFromViewport(
    width: number,
    height: number,
    fov: number,
    _isMobile: boolean,
    curtainBoundaryPx: number,
    viewOffsetTopPx: number,
    config: ResponsiveTransformConfig
  ): void {
    if (this.disposed) {
      return;
    }

    // The z-distance from camera (fixed)
    const zDistance = -0.02;
    
    // Calculate visible dimensions at the z-distance
    // For perspective camera: visibleHeight = 2 * |z| * tan(fov/2)
    const fovRad = (fov * Math.PI) / 180;
    const visibleHeight = 2 * Math.abs(zDistance) * Math.tan(fovRad / 2);
    const visH2 = visibleHeight / 2;
    const aspect = width / height;
    const visibleWidth = visibleHeight * aspect;

    // Convert pixel padding to camera-local coordinates
    // paddingRight > 0: position from right edge; paddingRight === 0: centered horizontally
    const localX = config.paddingRight > 0
      ? visibleWidth / 2 - (config.paddingRight / width) * visibleWidth
      : 0;

    // Compute the camera's principal-point Y shift (py) — mirrors
    // PrincipalPointController.applyViewOffset so we can compensate for it.
    // py shifts the projection matrix; without compensation the ball drifts
    // from the intended screen position.
    const centerY = viewOffsetTopPx + (height - viewOffsetTopPx - curtainBoundaryPx) / 2;
    const py = (centerY / height) * 2 - 1;

    // Target screen position: curtain boundary + vertical offset (+ = up, - = down)
    const bottomPx = curtainBoundaryPx + config.verticalOffset;
    const targetNdcY = (bottomPx / height) * 2 - 1;

    // Camera-local Y that maps to targetNdcY after the shifted projection.
    // Derivation: ndc_y = localY / visH2 - py  =>  localY = (ndc_y + py) * visH2
    const localY = (targetNdcY + py) * visH2;

    // Update the assistant position and scale
    this.updateTransform({ x: localX, y: localY, z: zDistance }, this.currentRotationY, config.scale);
    
    // Update hover amplitude from config
    this.params.hoverAmplitude = config.hoverAmplitude;
  }

  updateTransform(position: { x: number; y: number; z: number }, rotationY: number, scale: number): void {
    if (this.disposed) {
      return;
    }

    this.currentPosition = position;
    this.currentRotationY = rotationY;
    this.currentScale = scale;

    // Reset smoothed local offset to zero to avoid artifacts
    this.smoothedLocalOffset.set(0, 0, 0);
    // Reset last camera state to force re-initialization
    this.lastCameraPosition.set(0, 0, 0);

    // Update mesh transform
    this.mesh.position.set(position.x, position.y, position.z);
    this.mesh.rotation.y = rotationY;
    this.mesh.scale.setScalar(scale);
    this.basePosition.copy(this.mesh.position);

    // Update shell transform
    this.shell.position.set(position.x, position.y, position.z);
    this.shell.rotation.y = rotationY;

    // Update shell uniforms for scale
    this.shellUniforms.uRadius.value = this.params.particlesRadius * scale;
    this.shellUniforms.uScale.value = this.params.particlesSize * 50 * scale;
  }

  updateMeshParams(params: Partial<typeof defaultParams>): void {
    if (this.disposed) {
      return;
    }

    if (params.gooScale !== undefined) {
      this.params.gooScale = params.gooScale;
      this.meshUniforms.gooScale.value = params.gooScale;
    }
    if (params.gooAmount !== undefined) {
      this.params.gooAmount = params.gooAmount;
      this.meshUniforms.gooAmount.value = params.gooAmount;
    }
    if (params.gooSpeed !== undefined) {
      this.params.gooSpeed = params.gooSpeed;
      this.meshUniforms.gooSpeed.value = params.gooSpeed;
    }
    if (params.gooPoleAmount !== undefined) {
      this.params.gooPoleAmount = params.gooPoleAmount;
      this.meshUniforms.gooPoleAmount.value = params.gooPoleAmount;
    }
    if (params.waveScale !== undefined) {
      this.params.waveScale = params.waveScale;
      this.meshUniforms.waveScale.value = params.waveScale;
    }
    if (params.waveFrequency !== undefined) {
      this.params.waveFrequency = params.waveFrequency;
      this.meshUniforms.waveFrequency.value = params.waveFrequency;
    }
    if (params.waveAmount !== undefined) {
      this.params.waveAmount = params.waveAmount;
      this.meshUniforms.waveAmount.value = params.waveAmount;
    }
    if (params.waveSpeed !== undefined) {
      this.params.waveSpeed = params.waveSpeed;
      this.meshUniforms.waveSpeed.value = params.waveSpeed;
    }
    if (params.wavePoleAmount !== undefined) {
      this.params.wavePoleAmount = params.wavePoleAmount;
      this.meshUniforms.wavePoleAmount.value = params.wavePoleAmount;
    }
    if (params.color !== undefined) {
      this.params.color = params.color;
      (this.meshUniforms.color.value as THREE.Color).set(params.color);
    }
    if (params.rotationSpeed !== undefined) {
      this.params.rotationSpeed = params.rotationSpeed;
    }
    if (params.hoverAmplitude !== undefined) {
      this.params.hoverAmplitude = params.hoverAmplitude;
    }
    if (params.showNormals !== undefined) {
      this.params.showNormals = params.showNormals;
      this.meshUniforms.showNormals.value = params.showNormals;
    }
    if (params.ambientLightColor !== undefined) {
      this.params.ambientLightColor = params.ambientLightColor;
      (this.meshUniforms.ambientLightColor.value as THREE.Color).set(params.ambientLightColor);
    }
    if (params.directionalLightColor !== undefined) {
      this.params.directionalLightColor = params.directionalLightColor;
      (this.meshUniforms.directionalLightColor.value as THREE.Color).set(params.directionalLightColor);
    }
    if (params.ambientLightIntensity !== undefined) {
      this.params.ambientLightIntensity = params.ambientLightIntensity;
      this.meshUniforms.ambientLightIntensity.value = params.ambientLightIntensity;
    }
    if (params.directionalLightIntensity !== undefined) {
      this.params.directionalLightIntensity = params.directionalLightIntensity;
      this.meshUniforms.directionalLightIntensity.value = params.directionalLightIntensity;
    }
  }

  updateShellParams(params: Partial<typeof defaultParams>): void {
    if (this.disposed) {
      return;
    }

    let needsRecreate = false;

    if (params.particlesCount !== undefined && params.particlesCount !== this.params.particlesCount) {
      this.params.particlesCount = params.particlesCount;
      needsRecreate = true;
    }
    if (params.particlesColor !== undefined) {
      this.params.particlesColor = params.particlesColor;
      (this.shellUniforms.uColor.value as THREE.Color).set(params.particlesColor);
    }
    if (params.particlesSize !== undefined) {
      this.params.particlesSize = params.particlesSize;
      this.shellUniforms.uScale.value = params.particlesSize * 50 * this.currentScale;
    }
    if (params.particlesRadius !== undefined) {
      this.params.particlesRadius = params.particlesRadius;
      this.shellUniforms.uRadius.value = params.particlesRadius * this.currentScale;
    }
    if (params.particlesNoiseScale !== undefined) {
      this.params.particlesNoiseScale = params.particlesNoiseScale;
      this.shellUniforms.uNoiseScale.value = params.particlesNoiseScale * 0.35;
    }
    if (params.particlesNoiseSpeed !== undefined) {
      this.params.particlesNoiseSpeed = params.particlesNoiseSpeed;
      this.shellUniforms.uNoiseSpeed.value = params.particlesNoiseSpeed;
    }
    if (params.particlesNoiseAmount !== undefined) {
      this.params.particlesNoiseAmount = params.particlesNoiseAmount;
      this.shellUniforms.uNoiseAmplitude.value = params.particlesNoiseAmount * 3.3;
    }

    if (needsRecreate && this.shell) {
      const parent = this.anchor ?? this.scene;
      parent.remove(this.shell);
      this.shell.geometry?.dispose();
      this.shell = this.createShell();
      this.shell.layers.set(this.renderLayer);
      this.shell.position.set(this.currentPosition.x, this.currentPosition.y, this.currentPosition.z);
      this.shell.rotation.y = this.currentRotationY;
      this.shell.visible = this.enabled;
      parent.add(this.shell);
    }
  }

  async updateLighting(lightingType: 'simple' | 'env', params: { lightX?: number; lightY?: number; lightZ?: number; ambientLightColor?: string; directionalLightColor?: string; ambientLightIntensity?: number; directionalLightIntensity?: number; environmentLightingIntensity?: number; environmentRotationY?: number }): Promise<void> {
    if (this.disposed) {
      return;
    }

    const wasEnvLighting = this.useEnvironmentLighting;
    const willBeEnvLighting = lightingType === 'env';

    // Update lighting type
    if (lightingType === 'simple') {
      this.useEnvironmentLighting = false;
      this.meshUniforms.useEnvironmentLighting.value = false;

      if (params.lightX !== undefined) {
        this.params.lightX = params.lightX;
      }
      if (params.lightY !== undefined) {
        this.params.lightY = params.lightY;
      }
      if (params.lightZ !== undefined) {
        this.params.lightZ = params.lightZ;
      }
      if (params.ambientLightColor !== undefined) {
        this.params.ambientLightColor = params.ambientLightColor;
        (this.meshUniforms.ambientLightColor.value as THREE.Color).set(params.ambientLightColor);
      }
      if (params.directionalLightColor !== undefined) {
        this.params.directionalLightColor = params.directionalLightColor;
        (this.meshUniforms.directionalLightColor.value as THREE.Color).set(params.directionalLightColor);
      }
      if (params.ambientLightIntensity !== undefined) {
        this.params.ambientLightIntensity = params.ambientLightIntensity;
        this.meshUniforms.ambientLightIntensity.value = params.ambientLightIntensity;
      }
      if (params.directionalLightIntensity !== undefined) {
        this.params.directionalLightIntensity = params.directionalLightIntensity;
        this.meshUniforms.directionalLightIntensity.value = params.directionalLightIntensity;
      }

      const lightDir = new THREE.Vector3(this.params.lightX, this.params.lightY, this.params.lightZ).normalize();
      (this.meshUniforms.lightDirection.value as THREE.Vector3).copy(lightDir);
    } else {
      this.useEnvironmentLighting = true;
      this.meshUniforms.useEnvironmentLighting.value = true;

      if (params.environmentLightingIntensity !== undefined) {
        this.environmentLightingIntensity = params.environmentLightingIntensity;
        this.meshUniforms.environmentLightingIntensity.value = params.environmentLightingIntensity;
      }

      if (params.environmentRotationY !== undefined) {
        const rotationChanged = this.environmentRotationY !== params.environmentRotationY;
        this.environmentRotationY = params.environmentRotationY;
        // Recalculate light probe when rotation changes (one-shot, not periodic)
        if (rotationChanged && this.lightProbeReady) {
          this.generateLightProbe();
        }
      }

      // Initialize cube camera if not already initialized
      if (!this.cubeCamera && !this.cubeRenderTarget) {
        this.cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256);
        this.cubeCamera = new THREE.CubeCamera(0.01, 1000, this.cubeRenderTarget);
      }

      // If switching from simple to env, need to initialize environment lighting
      if (!wasEnvLighting && willBeEnvLighting) {
        await this.loadPanoramaTexture();
      }
    }
  }

  private async loadPanoramaTexture(): Promise<void> {
    // Initialize cube camera if not already initialized
    if (!this.cubeRenderTarget) {
      this.cubeRenderTarget = new THREE.WebGLCubeRenderTarget(256);
    }
    if (!this.cubeCamera) {
      this.cubeCamera = new THREE.CubeCamera(0.01, 1000, this.cubeRenderTarget);
    }

    // Load panorama HDR texture if URL is provided
    if (this.panoramaTextureUrl) {
      try {
        const loader = new HDRLoader();
        const texture = await loader.loadAsync(this.panoramaTextureUrl);
        texture.mapping = THREE.EquirectangularReflectionMapping;

        if (this.disposed) {
          texture.dispose();
          return;
        }

        this.panoramaTexture = texture;
      } catch (error) {
        console.warn('Failed to load panorama HDR texture, falling back to scene background:', error);
      }
    }

    // Generate light probe from panorama texture or scene background (once at load)
    await this.generateLightProbe();
    this.lightProbeReady = true;
  }

  private async generateLightProbe(): Promise<void> {
    if (!this.cubeCamera || !this.cubeRenderTarget) {
      return;
    }

    // Reuse cached empty scene to avoid allocation each time
    if (!this.cachedEmptyScene) {
      this.cachedEmptyScene = new THREE.Scene();
    }
    this.cachedEmptyScene.background = this.panoramaTexture ?? this.scene.background;
    this.cachedEmptyScene.backgroundRotation.set(0, this.environmentRotationY, 0);

    this.scene.add(this.cubeCamera);

    // Update cube camera position to mesh position
    this.mesh.getWorldPosition(this.cubeCamera.position);
    this.cubeCamera.updateMatrixWorld(true);

    // Capture environment from cached empty scene (only background, no objects)
    this.cubeCamera.update(this.renderer, this.cachedEmptyScene);

    // Remove cube camera from scene
    this.scene.remove(this.cubeCamera);

    // Generate light probe from cube render target
    try {
      const probe = await LightProbeGenerator.fromCubeRenderTarget(this.renderer, this.cubeRenderTarget);
      if (this.disposed) return;

      this.lightProbe = probe;

      // Update shader uniforms with SH coefficients
      if (this.meshUniforms && this.meshUniforms.shLightProbe) {
        const shCoefficients = this.meshUniforms.shLightProbe.value as THREE.Vector3[];
        const coefficients = probe.sh.coefficients;
        for (let i = 0; i < 9; i++) {
          shCoefficients[i].copy(coefficients[i]);
        }
        // Mark material as needing update
        if (this.mesh.material instanceof THREE.ShaderMaterial) {
          this.mesh.material.uniformsNeedUpdate = true;
        }
      }
    } catch (error) {
      console.error('Failed to generate light probe:', error);
    }
  }


  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;

    const parent = this.anchor ?? this.scene;
    
    if (this.mesh) {
      parent.remove(this.mesh);
      this.mesh.geometry?.dispose();
      if (this.mesh.material instanceof THREE.Material) {
        this.mesh.material.dispose();
      }
    }

    if (this.shell) {
      parent.remove(this.shell);
      this.shell.geometry?.dispose();
      if (this.shell.material instanceof THREE.Material) {
        this.shell.material.dispose();
      }
    }

    // Clean up environment lighting resources
    if (this.cubeCamera) {
      this.scene.remove(this.cubeCamera);
    }
    if (this.cubeRenderTarget) {
      this.cubeRenderTarget.dispose();
    }
    if (this.panoramaTexture) {
      this.panoramaTexture.dispose();
    }
  }

  private createMesh(): THREE.Mesh {
    // Create geometry with radius 1.0 - deformations work with normalized coordinates
    // Scale will be applied via object.scale in load()
    // Quality-dependent segments: 64x64 desktop (8K verts), 48x48 mobile (4.6K verts)
    // Deformations are low-frequency (goo + wave), so high segment counts are unnecessary
    const radius = 1.0;
    const segments = this.isMobile ? 48 : 64;
    const rings = this.isMobile ? 48 : 64;
    const geometry = new THREE.SphereGeometry(radius, segments, rings);

    const material = new THREE.ShaderMaterial({
      uniforms: this.meshUniforms,
      vertexShader,
      fragmentShader,
      side: THREE.FrontSide // Closed sphere — back faces are never visible
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'teller-assistant-mesh';
    mesh.visible = false; // Hidden until explicitly enabled after scene reveal
    return mesh;
  }

  private createShell(): THREE.Points {
    const geometry = this.generateParticlesSphere(this.params.particlesCount, this.params.particlesRadius);

    const material = new THREE.ShaderMaterial({
      uniforms: this.shellUniforms,
      vertexShader: shellVertexShader,
      fragmentShader: shellFragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending
    });

    const points = new THREE.Points(geometry, material);
    points.name = 'teller-assistant-shell';
    points.visible = false; // Hidden until explicitly enabled after scene reveal
    return points;
  }

  private generateParticlesSphere(count: number, radius: number): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    
    // Use Fibonacci sphere algorithm for even distribution
    const goldenAngle = Math.PI * (3 - Math.sqrt(5)); // Golden angle in radians
    
    for (let i = 0; i < count; i++) {
      const y = 1 - (i / (count - 1)) * 2; // y goes from 1 to -1
      const theta = goldenAngle * i; // golden angle increment
      
      // Calculate spherical coordinates
      // t (theta) - azimuthal angle [0, 2π] → position.x
      // v (phi) - polar angle [0, π] → position.y
      const phi = Math.acos(y); // polar angle [0, PI]
      const theta2 = theta; // azimuthal angle [0, 2PI]
      
      // Store spherical coordinates (t, v) in position attribute
      // This matches the reference implementation where position.x = t and position.y = v
      positions[i * 3] = theta2; // t (theta) - azimuthal angle
      positions[i * 3 + 1] = phi; // v (phi) - polar angle
      positions[i * 3 + 2] = 0; // unused, can be used for additional data
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }

  private setInitialTransform(object: THREE.Object3D): void {
    const position = this.position ?? { x: 0, y: 0, z: 0 };
    object.position.set(position.x, position.y, position.z);
    if (typeof this.rotationY === 'number') {
      object.rotation.y = this.rotationY;
    } else {
      object.rotation.y = Math.PI * 0.8;
    }
  }
}
